import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Pencil, Archive, ArchiveRestore, Plus, Package, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/roles";
import { StatsGrid } from "@/components/wh/StatsGrid";
import { LotDialog } from "@/components/wh/LotDialog";
import { ItemSearch } from "@/components/wh/ItemSearch";
import { useWh } from "@/lib/wh";
import { useNavigate } from "react-router-dom";

export default function WhLot() {
  const { id } = useParams();
  const { user } = useAuth();
  const [lot, setLot] = useState(null);
  const [edit, setEdit] = useState(false);
  const [lots, setLots] = useState([]);
  const [err, setErr] = useState("");
  const { wh, setWh } = useWh();
  const nav = useNavigate();
  const load = useCallback(async () => {
    try { setLot((await api.get(`/wh/lots/${id}`)).data); } catch (e) { setErr(formatApiError(e)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!lot?.wh) return;
    if (lot.wh !== wh) setWh(lot.wh);
    api.get("/wh/lots", { params: { wh: lot.wh } }).then(({ data }) => setLots(data)).catch(() => {});
  }, [lot?.wh]); // eslint-disable-line react-hooks/exhaustive-deps
  const del = async () => {
    if (window.prompt(`Eliminare definitivamente il lotto ${lot.name} e tutti i suoi ${lot.stats.items} articoli? Nello storico resta traccia. Scrivi ELIMINA per confermare.`) !== "ELIMINA") return;
    try { const { data } = await api.delete(`/wh/lots/${id}`); toast.success(`Eliminati il lotto e ${data.items} articoli`); nav("/magazzino"); } catch (e) { toast.error(formatApiError(e)); }
  };
  const archive = async () => {
    if (!lot.archived && !window.confirm("Archiviare il lotto? Articoli e storico restano consultabili.")) return;
    try { await api.post(`/wh/lots/${id}/archive`, null, { params: { archived: !lot.archived } }); toast.success(lot.archived ? "Lotto riattivato" : "Lotto archiviato"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  if (err) return <div className="space-y-3" data-testid="wh-lot-error"><p>{err}</p><Link to="/magazzino" className="underline">Torna al magazzino</Link></div>;
  if (!lot) return <p className="text-[var(--muted)]">Caricamento...</p>;
  return (
    <div className="space-y-8">
      <Link to="/magazzino" className="text-sm text-[var(--muted)] flex items-center gap-1" data-testid="wh-back"><ArrowLeft className="h-4 w-4" /> Magazzino</Link>
      <div className="flex flex-col sm:flex-row gap-5 sm:items-center fade-up">
        {lot.photo_path ? <img src={fileUrl(lot.photo_path)} alt="" className="h-24 w-24 rounded-2xl object-cover border" />
          : <div className="h-24 w-24 rounded-2xl border bg-[var(--surface-2)] grid place-items-center"><Package className="h-8 w-8 text-[var(--muted)]" /></div>}
        <div className="flex-1 min-w-0">
          <p className="eyebrow">{lot.archived ? "Lotto archiviato" : "Lotto"}</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight break-words" data-testid="wh-lot-name">{lot.name}</h1>
          {lot.description && <p className="text-sm text-[var(--muted)] mt-1 whitespace-pre-line">{lot.description}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          {can(user, "wh_write") && !lot.archived && <Link to={`/magazzino/articoli/nuovo?lot=${id}`} className="btn-primary h-11 px-4" data-testid="wh-lot-new-item"><Plus className="h-5 w-5" /> Articolo</Link>}
          {can(user, "wh_manage") && <button onClick={() => setEdit(true)} className="btn-secondary h-11 px-4" data-testid="wh-lot-edit"><Pencil className="h-4 w-4" /> Modifica</button>}
          {can(user, "wh_manage") && <button onClick={archive} className="btn-secondary h-11 px-4" data-testid="wh-lot-archive">{lot.archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />} {lot.archived ? "Riattiva" : "Archivia"}</button>}
          {user.role === "admin" && <button onClick={del} className="btn-secondary h-11 px-4 text-red-600" data-testid="wh-lot-delete"><Trash2 className="h-4 w-4" /> Elimina lotto</button>}
        </div>
      </div>
      <StatsGrid stats={lot.stats} />
      <ItemSearch lotId={id} lots={lots} title={`lotto ${lot.name}`} onChanged={load} />
      <LotDialog open={edit} onOpenChange={setEdit} lot={lot} onSaved={load} />
    </div>
  );
}
